/**
 * Entrada a StakeMap.
 *
 * Es también el destino al que vuelve el enlace de confirmación del correo,
 * que llega con `?confirmada=1` o con `?error=<code>`; por eso la pantalla
 * sabe dar esas dos noticias además de pedir las credenciales.
 */

import { KeyRound, LogIn, Network } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ErrorApi } from '../api/cliente'
import { useEntrar } from '../api/sesion'
import logoCircularTec from '../assets/circulartec.png'
import { Boton, Campo, Entrada, Panel } from '../ui/primitivos'

const AVISOS_DEL_ENLACE: Record<string, string> = {
  // El backend no distingue por qué falló el enlace —hacerlo permitiría
  // averiguar qué cuentas existen—, así que el aviso cubre los tres casos.
  enlace_invalido:
    'Este enlace de confirmación ya no sirve: o caducó, o ya se usó. Si ya confirmaste tu cuenta, entra normalmente; si no, vuelve a registrarte.',
}

export function Login() {
  const navegar = useNavigate()
  const [params] = useSearchParams()
  const entrar = useEntrar()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const confirmada = params.get('confirmada') === '1'
  const avisoEnlace = AVISOS_DEL_ENLACE[params.get('error') ?? '']

  function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    entrar.mutate(
      { email: email.trim(), password },
      // El §1.b pide volver a la raíz tras autenticarse.
      { onSuccess: () => navegar('/', { replace: true }) },
    )
  }

  const error = entrar.error instanceof ErrorApi ? entrar.error : undefined
  // El serializer devuelve los errores del formulario bajo `errors`; los de
  // credenciales llegan sin campo, en `non_field_errors`.
  const mensaje = error?.errors?.non_field_errors?.join(' ') ?? (error ? error.message : '')

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 p-4">
      <div className="flex flex-col items-center gap-3">
        {/* Aquí el logo va más grande que en la cabecera: es la primera
            pantalla y no compite con nada. El filtro levanta su azul en tema
            oscuro, donde contrasta 2,6:1 contra el fondo. */}
        <img
          src={logoCircularTec}
          alt="CircularTec"
          width={250}
          height={67}
          className="h-9 w-auto dark:brightness-[1.5] dark:saturate-[1.15]"
        />
        <div className="flex items-center gap-2">
          <Network size={20} className="text-[var(--color-acento)]" />
          <span className="text-[18px] font-semibold tracking-tight">StakeMap</span>
        </div>
      </div>

      {confirmada && (
        <p className="rounded-md border border-[var(--color-favorable)] bg-[var(--color-panel)] px-3 py-2 text-[13px] text-[var(--color-favorable)]">
          Tu cuenta quedó activada. Ya puedes entrar.
        </p>
      )}
      {avisoEnlace && (
        <p className="rounded-md border border-[var(--color-opositora)] bg-[var(--color-panel)] px-3 py-2 text-[13px] text-[var(--color-opositora)]">
          {avisoEnlace}
        </p>
      )}

      <Panel titulo="Entrar">
        <form onSubmit={enviar} className="grid gap-3 p-3">
          <Campo etiqueta="Correo">
            <Entrada
              autoFocus
              required
              type="email"
              autoComplete="username"
              placeholder="tu.nombre@tuempresa.cl"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Campo>

          <Campo etiqueta="Contraseña">
            <Entrada
              required
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Campo>

          {mensaje && <p className="text-[12px] text-[var(--color-opositora)]">{mensaje}</p>}

          <Boton
            type="submit"
            variante="primario"
            icono={<LogIn size={14} />}
            cargando={entrar.isPending}
            disabled={!email.trim() || !password}
          >
            Entrar
          </Boton>
        </form>
      </Panel>

      <p className="text-center text-[13px] text-[var(--color-texto-suave)]">
        ¿No tienes cuenta?{' '}
        <Link to="/registro" className="text-[var(--color-acento)] underline">
          Regístrate
        </Link>
      </p>

      <p className="flex items-center justify-center gap-1.5 text-[11px] text-[var(--color-texto-tenue)]">
        <KeyRound size={11} />
        Solo correos corporativos o institucionales.
      </p>
    </div>
  )
}
